import CheckoutSteps from '../../components/checkout/CheckoutSteps';
import { Badge, Button, Card, Col, Container, Form, Image, ListGroup, Modal, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState, useMemo } from 'react';
import FormContainer from '../../components/form/FormContainer';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import PayPhoneBox from '../../components/checkout/PayPhoneBox';
import useExitGuard, { allowNextNavigation } from '../../utils/useExitGuard';
import {
    useValidateCartStockMutation,
    useUpdateCartMutation,
} from '../../slices/cartApiSlice';
import { useCreateQuotationMutation } from '../../slices/orderApiSlice';
import { savePaymentMethod, resetCart, saveBillingAddress } from '../../slices/cartSlice';
import { clearActiveOrder } from '../../slices/orderSlice';
import { toast } from 'react-toastify';
import ProductPrice from '../../components/product/ProductPrice';
import TotalPrice from '../../components/product/TotalPrice';
import ContificoCodeBadge from '../../components/ui/ContificoCodeBadge';
import { FaWhatsapp } from 'react-icons/fa6';
import { FaEdit, FaHome, FaUserAlt, FaSyncAlt, FaExclamationTriangle, FaEraser, FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import AddressEditForm from '../../components/checkout/AddressEditForm';
import ShippingAddress from '../../components/checkout/ShippingAddress';
import BillingAddress from '../../components/checkout/BillingAddress';
import StickyOrderSummary from '../../components/checkout/StickyOrderSummary';
import OrderPriceSummary from '../../components/checkout/OrderPriceSummary';
import { selectActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import { discountSummaryLabel, resolveSummaryDiscount } from '../../utils/activeGlobalDiscount';
import { isShippingPending, shippingSummaryText } from '../../utils/shippingBehavior';
import useIsMobile from '../../utils/useIsMobile';
import '../../assets/styles/order.screen.css';
import { groupOrderItems } from '../../utils/cartUtils';
import { FreeShippingBadge, ProductRowBenefitNotes } from '../../components/product/ProductBenefitMarks';

const PlaceOrderScreen = () => {
    useExitGuard(true);

    const isMobile = useIsMobile();
    const paymentRef = useRef(null);
    const addressFormRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(true);

    const { userInfo } = useSelector((state) => state.auth);
    const { companiesByRuc } = useSelector((state) => state.companies);
    const activeGlobalDiscount = useSelector(selectActiveGlobalDiscount);

    const navigate = useNavigate();
    const cart = useSelector((state) => state.cart);
    const { selectedCompanyRuc } = cart;
    const { activeOrder } = useSelector((state) => state.cart);

    // Support both new object shape and legacy/server array shape
    const groupedItems = useMemo(() => {
        return groupOrderItems(activeOrder, companiesByRuc);
    }, [activeOrder, companiesByRuc]);

    const totalCompanies = Object.keys(groupedItems).length;

    const selectedGroup = selectedCompanyRuc ? groupedItems[selectedCompanyRuc] : null;
    const selectedCompanyInfo = selectedCompanyRuc
        ? {
            ...(groupedItems[selectedCompanyRuc]?.companyInfo || {}),
            ...(companiesByRuc?.[selectedCompanyRuc] || {}),
        }
        : null;

    // Calculate global totals from groupedItems since backend might not provide root totals
    const globalItemsPrice = Object.values(groupedItems).reduce((acc, group) => acc + (Number(group.itemsPrice) || 0), 0);
    const globalDiscountAmount = Object.values(groupedItems).reduce((acc, group) => acc + (Number(group.discountAmount) || 0), 0);
    const globalPreDiscountItemsPrice = Object.values(groupedItems).reduce((acc, group) => {
        const groupItemsPrice = Number(group.itemsPrice) || 0;
        const groupDiscountAmount = Number(group.discountAmount) || 0;
        return acc + (Number(group.preDiscountItemsPrice) || (groupDiscountAmount > 0 ? groupItemsPrice + groupDiscountAmount : groupItemsPrice));
    }, 0);
    const globalShippingPrice = Object.values(groupedItems).reduce((acc, group) => acc + (Number(group.shippingPrice) || 0), 0);
    const globalTaxPrice = Object.values(groupedItems).reduce((acc, group) => acc + (Number(group.taxPrice) || 0), 0);
    const globalTotalPrice = Object.values(groupedItems).reduce((acc, group) => acc + (Number(group.totalPrice) || 0), 0);

    const finalItemsPrice = selectedGroup
        ? selectedGroup.itemsPrice
        : globalItemsPrice;

    const finalDiscountAmount = selectedGroup
        ? Number(selectedGroup.discountAmount) || 0
        : globalDiscountAmount;

    const finalPreDiscountItemsPrice = selectedGroup
        ? Number(selectedGroup.preDiscountItemsPrice) || ((Number(selectedGroup.itemsPrice) || 0) + (Number(selectedGroup.discountAmount) || 0))
        : globalPreDiscountItemsPrice;

    const finalShippingPrice = selectedGroup
        ? selectedGroup.shippingPrice
        : globalShippingPrice;

    const finalTaxPrice = selectedGroup
        ? selectedGroup.taxPrice
        : globalTaxPrice;

    const finalTotalPrice = selectedGroup
        ? selectedGroup.totalPrice
        : globalTotalPrice;
    const summaryDiscount = selectedGroup
        ? resolveSummaryDiscount({
            activeGlobalDiscount,
            companyRuc: selectedCompanyRuc,
            appliedCoupon: selectedGroup.appliedCoupon,
        })
        : null;
    const discountLabel = discountSummaryLabel(summaryDiscount);
    const shippingPending = isShippingPending({
        shippingPrice: finalShippingPrice,
        address: cart.shippingAddress,
    });
    const shippingLabel = shippingSummaryText({
        shippingPrice: finalShippingPrice,
        pending: shippingPending,
    });

    // Calculate taxable and non-taxable bases for PayPhone
    const { taxableBase, nonTaxableBase } = useMemo(() => {
        if (!selectedGroup) return { taxableBase: 0, nonTaxableBase: 0 };

        const totalBase = Number(finalItemsPrice) + Number(finalShippingPrice);
        const totalTax = Number(finalTaxPrice) || 0;

        // SAFEGUARD 1: If total tax is 0, PayPhone requires amountWithTax to be 0.
        if (totalTax === 0) {
            return {
                taxableBase: 0,
                nonTaxableBase: totalBase
            };
        }

        // Simplified logic: Assume everything is taxable if there is tax
        return {
            taxableBase: totalBase,
            nonTaxableBase: 0
        };
    }, [selectedGroup, finalItemsPrice, finalShippingPrice, finalTaxPrice]);

    const [showModal, setShowModal] = useState(false);

    const [paymentMethod, setPaymentMethod] = useState(() => {
        // If user is a distributor, no default payment method yet
        if (userInfo?.priceAssigned === 'pvp_distribuidor') {
            return activeOrder?.paymentMethod || cart.paymentMethod || '';
        }
        // For non-distributors, default to PayPhone
        return 'PayPhone';
    });
    const [observations, setObservations] = useState(activeOrder?.observations || cart.observations || '');

    const [updateCart, { isLoading: isLoadingUpdateCart }] = useUpdateCartMutation();
    const [createQuotation, { isLoading: isLoadingCreateQuotation }] = useCreateQuotationMutation();
    const [validateCartStock] = useValidateCartStockMutation();

    const [showStockModal, setShowStockModal] = useState(false);
    const [unavailableItems, setUnavailableItems] = useState([]);
    const [isLoadingSendQuotation, setIsLoadingSendQuotation] = useState(false);

    const creatingOrder = useRef(false);
    const isNavigatingToOrder = useRef(false);
    const dispatch = useDispatch();

    // Generate a unique clientTransactionId for PayPhone
    // This ID is generated once per mount/session to identify this payment attempt
    const [clientTransactionId] = useState(() => selectedGroup?.id);

    const [billingAddress, setBillingAddress] = useState(cart.billingAddress && cart.billingAddress.docId ? cart.billingAddress : {
        ...userInfo,
        name:
            cart.shippingAddress.label === 'pickup'
                ? userInfo.name
                : cart.shippingAddress.name || '',
        docId:
            cart.shippingAddress.label === 'pickup'
                ? userInfo.docId
                : cart.shippingAddress.docId || '',
        taxDocId:
            cart.shippingAddress.label === 'pickup'
                ? userInfo.taxDocId
                : cart.shippingAddress.taxDocId || '',
        type:
            cart.shippingAddress.label === 'pickup'
                ? userInfo.type
                : cart.shippingAddress.type || '',
        email:
            cart.shippingAddress.label === 'pickup'
                ? userInfo.email
                : cart.shippingAddress.email || '',
        address1: cart.shippingAddress.label === 'pickup'
            ? 'NA'
            : cart.shippingAddress.address1,
        phoneNumber:
            cart.shippingAddress.label === 'pickup'
                ? '0999999999'
                : cart.shippingAddress.phoneNumber || '',
    });

    const isBillingReady = !!(billingAddress.docId || billingAddress.taxDocId);

    const [billingOption, setBillingOption] = useState(
        cart.shippingAddress.label === 'pickup' ? 'own' : 'same'
    ); // 'same', 'own'

    useEffect(() => {
        dispatch(saveBillingAddress(billingAddress));
    }, [billingAddress, dispatch]);

    const handleNewBillingAddressSubmit = async (formData) => {
        setBillingAddress(formData);
        setShowModal(false);
        toast.success('Datos de facturación guardados con éxito.')
    };

    const handlePaymentMethodChange = (e) => {
        const value = e.target.value;
        dispatch(savePaymentMethod(value));
        setPaymentMethod(value);
    };

    const handleEditOrder = () => {
        setShowStockModal(false);
        navigate('/cart');
    };

    useEffect(() => {
        if (isNavigatingToOrder.current) {
            return; // Don't redirect if we're navigating to order page
        }
        if (!cart.shippingAddress.address1) {
            navigate('/shipping');
        } else if (!selectedCompanyRuc) {
            navigate('/cart');
        }
    }, [cart.paymentMethod, cart.shippingAddress.address1, selectedCompanyRuc, navigate]);

    useEffect(() => {
        const target = paymentRef.current;

        if (!target) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowScrollButton(!entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        observer.observe(target);

        return () => {
            observer.unobserve(target);
        };
    }, []);

    // Handler for distributors to send quotation via WhatsApp
    const sendQuotationHandler = async () => {
        if (creatingOrder.current) return;
        try {
            if (!isBillingReady) {
                toast.error('Por favor, completa los datos de facturación antes de continuar.');
                return;
            }

            creatingOrder.current = true;
            setIsLoadingSendQuotation(true);

            // Validate stock first
            const validationRes = await validateCartStock({ companyId: selectedCompanyRuc }).unwrap();

            if (!validationRes.ok && !validationRes.success) {
                setUnavailableItems(validationRes.unavailableItems || []);
                setShowStockModal(true);
                creatingOrder.current = false;
                setIsLoadingSendQuotation(false);
                return;
            }

            // Update cart with latest info
            await updateCart({
                billingAddress,
                paymentMethod: 'Quotation',
                observations
            }).unwrap();

            const quotationPayload = {
                billingAddress,
                paymentMethod: 'Quotation',
                observations,
                companyRuc: selectedCompanyRuc,
                fastPricing: false
            };

            // Create the quotation (order without payment)
            const res = await createQuotation(quotationPayload).unwrap();

            creatingOrder.current = false;
            setIsLoadingSendQuotation(false);

            // Navigate to order details
            const target = `/order/${res._id}`;
            isNavigatingToOrder.current = true; // Set flag before navigation
            allowNextNavigation(target);
            dispatch(clearActiveOrder());
            dispatch(resetCart());
            navigate(target);

        } catch (e) {
            creatingOrder.current = false;
            setIsLoadingSendQuotation(false);
            toast.error(e?.data?.message || e.message || 'Error al procesar la cotización');
        }
    };


    return (
        <Container className="pb-5">
            <Meta title="Realizar Pedido" />
            <FormContainer>
                <CheckoutSteps orderScreen />
            </FormContainer>
            <h1 className="my-4 text-center fw-bold">Realizar Pedido</h1>
            {selectedCompanyRuc && groupedItems[selectedCompanyRuc] && (
                <div className="text-center mb-4">
                    <h5 className="text-muted">
                        Pago correspondiente a <span className="text-primary fw-bold">{groupedItems[selectedCompanyRuc].companyInfo?.tradeName || groupedItems[selectedCompanyRuc].name || 'Comercial Santy'}</span>
                    </h5>
                </div>
            )}
            <Row className=" gx-lg-5">
                <Col lg={8}>
                    <Card className="section-card shadow-sm mb-4">
                        <div className="section-header d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 fs-5">
                                {cart.shippingAddress?.label === 'pickup' ? 'Retiro en tienda' : 'Dirección de Envío'}
                            </h4>
                        </div>
                        <ListGroup variant="flush">
                            <ListGroup.Item className="p-3 p-md-4">
                                <ShippingAddress
                                    address={cart.shippingAddress}
                                    isPaid={false}
                                    pickupCompanyInfo={selectedCompanyInfo}
                                />
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>

                    <Card className="section-card shadow-sm mb-4">
                        <div className="section-header d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 fs-5">Datos de Facturación</h4>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                    setShowModal(true);
                                }}
                                className="d-flex align-items-center gap-1"
                            >
                                <FaEdit size={16} /> <span className="d-none d-sm-inline">Editar</span>
                            </Button>
                        </div>
                        <ListGroup variant="flush">
                            <ListGroup.Item className="p-3 p-md-4">
                                <div className="billing-options-grid">
                                    <div
                                        className={`billing-option-card ${billingOption === 'own' ? 'selected' : ''}`}
                                        onClick={() => {
                                            setBillingOption('own');
                                            setBillingAddress({
                                                ...userInfo,
                                                type: userInfo.type || 'N',
                                                address1: 'NA',
                                                phoneNumber: '9999999999',
                                            });
                                        }}
                                    >
                                        <Form.Check
                                            type="radio"
                                            id="billing-own"
                                            name="billingOption"
                                            value="own"
                                            label={
                                                <span>
                                                    <FaUserAlt className={`me-2 ${billingOption === 'own' ? 'text-primary' : 'text-muted'}`} />
                                                    Mis Datos
                                                </span>
                                            }
                                            checked={billingOption === 'own'}
                                            onChange={() => {}} // Handled by div onClick
                                        />
                                    </div>

                                    <div
                                        className={`billing-option-card ${billingOption === 'same' ? 'selected' : ''} ${cart.shippingAddress.label === 'pickup' ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (cart.shippingAddress.label !== 'pickup') {
                                                setBillingOption('same');
                                                setBillingAddress({
                                                    ...cart.shippingAddress,
                                                });
                                            }
                                        }}
                                    >
                                        <Form.Check
                                            type="radio"
                                            id="billing-same"
                                            name="billingOption"
                                            value="same"
                                            disabled={cart.shippingAddress.label === 'pickup'}
                                            label={
                                                <span>
                                                    <FaHome className={`me-2 ${billingOption === 'same' ? 'text-primary' : 'text-muted'}`} />
                                                    {cart.shippingAddress.label === 'pickup' ? 'No aplica en retiro' : 'Igual a Envío'}
                                                </span>
                                            }
                                            checked={billingOption === 'same'}
                                            onChange={() => {}} // Handled by div onClick
                                        />
                                    </div>

                                </div>

                                <div className="d-flex flex-column gap-2">
                                    {(billingOption === 'new' || billingOption === 'own') && (
                                        <div className="bg-light p-3 rounded">
                                            <BillingAddress billingAddress={billingAddress} />
                                        </div>
                                    )}
                                </div>
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>

                    <Card className="section-card shadow-sm mb-4">
                        <div className="section-header">
                            <h4 className="mb-0 fs-5">Resumen del Carrito</h4>
                        </div>
                        <ListGroup variant="flush">
                            <ListGroup.Item className="p-0">
                                {!selectedGroup || selectedGroup.items.length === 0 ? (
                                    <div className="p-4">
                                        <Message>Tu carrito está vacío</Message>
                                    </div>
                                ) : (
                                    <div className="p-3 p-md-4">
                                        <ListGroup variant="flush">
                                            {selectedGroup.items.map((item, index) => (
                                                <ListGroup.Item key={index} className="py-3 border-bottom-0 cart-item-row place-order-item-row">
                                                    <Row className="align-items-start align-items-sm-center gy-2 gy-sm-3 gx-2 gx-sm-3">
                                                        <Col xs={4} sm={2} className="text-center">
                                                            <Image
                                                                fluid
                                                                rounded
                                                                src={item.image || '/images/default.jpg'}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = '/images/default.jpg';
                                                                }}
                                                                alt={item.name}
                                                                style={{ maxWidth: '80px', objectFit: 'cover' }}
                                                            />
                                                        </Col>

                                                        <Col xs={8} sm={6} className="ps-1 ps-sm-4 place-order-item-info">
                                                            <Link
                                                                to={`/product/${item.product}`}
                                                                className="fw-bold d-block text-dark text-decoration-none mb-1 place-order-item-title"
                                                            >
                                                                {item.name}
                                                            </Link>
                                                            <FreeShippingBadge product={item} companyRuc={selectedCompanyRuc} />
                                                            <ProductRowBenefitNotes product={item} companyRuc={selectedCompanyRuc} />
                                                            <ContificoCodeBadge
                                                                code={item?.contificoCode}
                                                                compact
                                                                className="mb-2 place-order-code-badge"
                                                            />
                                                            {item.selectedVariations?.map((v, i) => (
                                                                <div key={i} className="small text-muted">
                                                                    <span className="fw-semibold">{v.variationType}:</span> {v.option}
                                                                </div>
                                                            ))}
                                                        </Col>

                                                        <Col xs={12} sm={4} className="text-end pt-1 pt-sm-0">
                                                            <ProductPrice
                                                                product={item}
                                                                userInfo={userInfo}
                                                                qty={item.qty}
                                                                size="medium"
                                                                className="justify-content-end"
                                                            />
                                                            {(() => {
                                                                const productDiscount = Number(item?.discount || 0);
                                                                const couponItemDiscount = Number(item?.couponDiscountAmount || item?.couponDiscount || 0);
                                                                return couponItemDiscount > 0 && productDiscount > 0 && productDiscount > couponItemDiscount;
                                                            })() && (
                                                                <Badge bg="info" className="mt-1">Mayor descuento aplicado</Badge>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    </div>
                                )}
                            </ListGroup.Item>
                            <ListGroup.Item className="p-3 p-md-4 bg-light border-top-0">
                                <Row className="align-items-center g-3">
                                    <Col xs={12} md={7}>
                                        <h5 className="mb-1 fw-bold">Total con IVA Incluido</h5>
                                        <p className="text-muted small mb-0">
                                            (Precios con {Number(process.env.REACT_APP_TAX_PERCENTAGE * 100)}% de IVA incluido)
                                        </p>
                                    </Col>
                                    <Col xs={12} md={5} className="text-end d-flex flex-column align-items-end">
                                        <TotalPrice
                                            size="xl"
                                            className="mb-0 justify-content-end"
                                            price={
                                                Number(finalItemsPrice) +
                                                Number(finalTaxPrice)
                                            }
                                        />
                                        {Number(finalDiscountAmount) > 0 && (
                                            <div className="text-success small mt-1 fw-semibold">
                                                {discountLabel} -${Number(finalDiscountAmount).toFixed(2)}
                                            </div>
                                        )}
                                        {shippingLabel === 'Gratis' && (
                                            <div className="text-success small mt-1">Envío gratis</div>
                                        )}
                                        {shippingLabel !== 'Gratis' && shippingLabel !== 'Pendiente por calcular' && (
                                            <div className="text-muted small mt-1">
                                                + {shippingLabel} envío
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </ListGroup.Item>
                        </ListGroup>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="order-summary-card p-4 shadow shadow-sm sticky-lg-top" style={{ top: '2rem', zIndex: 10 }}>
                        <h2 className="order-summary-title">Resumen de Orden</h2>
                        {selectedCompanyRuc && (
                            <div className="mb-3 small text-primary fw-bold border-bottom pb-2">
                                Pago correspondiente a {groupedItems[selectedCompanyRuc]?.companyInfo?.tradeName || 'Comercial Santy'}
                            </div>
                        )}
                        
                        <OrderPriceSummary
                            subtotal={finalPreDiscountItemsPrice}
                            discountAmount={finalDiscountAmount}
                            discount={summaryDiscount}
                            shippingPrice={finalShippingPrice}
                            shippingPending={shippingPending}
                            taxPrice={finalTaxPrice}
                            total={(
                                <TotalPrice
                                    price={finalTotalPrice}
                                    size="xl"
                                    className="justify-content-end"
                                />
                            )}
                        />

                        <ListGroup variant="flush">
                            <div id="payment-section" ref={paymentRef} className="px-3 pb-3 pt-2">
                                <ListGroup.Item className="border rounded bg-white p-3 mb-3 shadow-sm">
                                    <h5 className="mb-3 fw-bold">{'Método de Pago'}</h5>
                                    <Form.Group>
                                        <div className="payment-methods-container">
                                            {userInfo.priceAssigned !== 'pvp_distribuidor' && (
                                                <div
                                                    className={`payment-method-card ${paymentMethod === 'PayPhone' ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        handlePaymentMethodChange({ target: { value: 'PayPhone' } });
                                                    }}
                                                >
                                                    <Form.Check
                                                        type="radio"
                                                        id="PayPhone"
                                                        name="paymentMethod"
                                                        value="PayPhone"
                                                        required={true}
                                                        checked={paymentMethod === 'PayPhone'}
                                                        onChange={() => {}} // Handled by div onClick
                                                        label={<span>PayPhone</span>}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Form.Group>
                                </ListGroup.Item>

                                <div
                                    className="payphone-wrapper p-2"
                                    style={{
                                        display: paymentMethod === 'PayPhone' ? 'block' : 'none',
                                    }}
                                >
                                    <PayPhoneBox
                                        amount={finalTotalPrice}
                                        amountWithTax={taxableBase}
                                        amountWithoutTax={nonTaxableBase}
                                        tax={finalTaxPrice}
                                        hidden={paymentMethod !== 'PayPhone'}
                                        clientTransactionId={clientTransactionId}
                                        companyId={selectedCompanyRuc}
                                    />
                                </div>

                                <div className="mt-3">
                                    {userInfo.priceAssigned === 'pvp_distribuidor' ? (
                                        <div className="distributor-wrapper">
                                            <Form.Group className="mb-3">
                                                <Form.Label className="small fw-bold" column='sm'>Instrucciones / Observaciones</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder="Agregue referencias de entrega o datos adicionales"
                                                    value={observations}
                                                    onChange={(e) => setObservations(e.target.value)}
                                                    style={{ borderColor: '#25D366' }}
                                                />
                                            </Form.Group>
                                            <Button
                                                type="button"
                                                variant="success"
                                                size="lg"
                                                className="w-100 py-3 d-flex justify-content-center align-items-center gap-2 shadow-sm rounded-pill fw-bold"
                                                style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                                                disabled={
                                                    totalCompanies === 0 ||
                                                    isLoadingUpdateCart ||
                                                    isLoadingCreateQuotation ||
                                                    isLoadingSendQuotation
                                                }
                                                onClick={sendQuotationHandler}
                                            >
                                                {isLoadingSendQuotation ? (
                                                    <>
                                                        <FaSpinner size={24} className="spinner" style={{ animation: 'spin 1s linear infinite' }} />
                                                        PROCESANDO...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaWhatsapp size={24} />
                                                        ENVIAR COTIZACIÓN
                                                    </>
                                                )}
                                            </Button>
                                            <style>{`
                                                @keyframes spin {
                                                    from { transform: rotate(0deg); }
                                                    to { transform: rotate(360deg); }
                                                }
                                                .spinner {
                                                    display: inline-block;
                                                }
                                            `}</style>
                                            <p className="text-center mt-3 small text-muted">
                                                Se creará su pedido y se abrirá WhatsApp para enviar la cotización.
                                            </p>
                                        </div>
                                    ) : (
                                        paymentMethod === 'PayPhone' && (
                                            <div className="payphone-note text-center small text-muted p-3 bg-light rounded">
                                                <p className="mb-0">Haga clic en el botón de PayPhone arriba para continuar con el pago</p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </ListGroup>
                    </Card>
                </Col>
            </Row>

            {isMobile && (
                <>
                    <div className="checkout-mobile-bar-spacer" aria-hidden="true" />
                    <StickyOrderSummary
                        discountLabel={discountLabel}
                        cart={{
                            totalPrice: finalTotalPrice,
                            preDiscountItemsPrice: finalPreDiscountItemsPrice,
                            discountAmount: finalDiscountAmount,
                        }}
                        actionLabel={showScrollButton ? 'Pagar ahora' : null}
                        onAction={() => {
                            const el = document.getElementById('payment-section');
                            if (el) {
                                el.scrollIntoView({ behavior: 'smooth' });
                                setShowScrollButton(false);
                            }
                        }}
                    />
                </>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" contentClassName="border-0 shadow-lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold">Otros Datos de Facturación</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light p-0">
                    <AddressEditForm
                        ref={addressFormRef}
                        userInfo={userInfo}
                        initialData={
                            cart.shippingAddress.label === 'pickup'
                                ? userInfo
                                : billingAddress
                        }
                        onSubmit={handleNewBillingAddressSubmit}
                        onCancel={() => {
                            setShowModal(false);
                        }}
                        useFor="billing"
                    />
                </Modal.Body>
                <Modal.Footer className="bg-white p-3 border-top">
                    <Row className="w-100 g-2 justify-content-md-end">
                        <Col xs={6} md="auto" className="order-2 order-md-1">
                            <Button
                                variant="outline-secondary"
                                onClick={() => addressFormRef.current?.wipeForm()}
                                className="w-100 d-flex align-items-center justify-content-center"
                            >
                                <FaEraser className="me-2" /> Limpiar
                            </Button>
                        </Col>
                        <Col xs={6} md="auto" className="order-3 order-md-2">
                            <Button
                                variant="outline-danger"
                                onClick={() => setShowModal(false)}
                                className="w-100 d-flex align-items-center justify-content-center"
                            >
                                <FaTimes className="me-2" /> Cancelar
                            </Button>
                        </Col>
                        <Col xs={12} md="auto" className="order-1 order-md-3">
                            <Button
                                type="submit"
                                form="address-edit-form"
                                variant="primary"
                                className="w-100 px-5 shadow-sm d-flex align-items-center justify-content-center"
                            >
                                <FaSave className="me-2" /> <strong>Guardar Datos</strong>
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Modal>
            <Modal show={showStockModal} onHide={() => setShowStockModal(false)} centered backdrop="static" keyboard={false}>
                <Modal.Header className="bg-danger text-white">
                    <Modal.Title className="fw-bold d-flex align-items-center">
                        <FaExclamationTriangle className="me-2" />
                        Stock No Disponible
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <style type="text/css">
                        {`
                            @keyframes pulse-red {
                                0% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.4); }
                                70% { box-shadow: 0 0 0 10px rgba(220, 53, 69, 0); }
                                100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0); }
                            }
                            .stock-issue-item {
                                animation: pulse-red 2s infinite;
                                border-left: 4px solid #dc3545 !important;
                                transition: all 0.3s ease;
                            }
                            .stock-issue-item:hover {
                                background-color: #fff5f5 !important;
                            }
                        `}
                    </style>
                    <p className="mb-3">
                        Lo sentimos, algunos productos en tu carrito ya no tienen el stock solicitado.
                        Por favor, revisa la lista a continuación:
                    </p>
                    <ListGroup variant="flush" className="border rounded">
                        {unavailableItems.map((item, index) => (
                            <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center p-3 bg-light stock-issue-item mb-2">
                                <div>
                                    <div className="fw-bold text-danger">{item.name}</div>
                                    <div className="small text-muted">
                                        Solicitado: <strong>{item.requested}</strong>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <Badge bg="danger" pill className="mb-2">Agotado</Badge>
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                    <div className="mt-4 text-center">
                        <Button 
                            variant="primary" 
                            onClick={handleEditOrder} 
                            className="w-100 py-2 fw-bold shadow-sm"
                            style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                        >
                            <FaSyncAlt className="me-2" /> Actualizar mi carrito
                        </Button>
                        <div className="mt-2 text-muted small">
                            Serás redirigido al carrito para ajustar las cantidades.
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
};
export default PlaceOrderScreen;

